/** Parse note HTML into title + preview for entry cards / breadcrumbs. */
export const getNotePreview = html => {
    if (!html) return { title: 'Untitled', preview: '' };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const tags = doc.body.children;
    const texts = [];

    for (let i = 0; i < tags.length && i < 2; i++) {
        const text = tags[i].textContent?.trim() || '';
        if (text) texts.push(text);
    }

    if (texts.length === 0) {
        const plain = (html.replace(/<[^>]+>/g, '') || '').trim();
        return {
            title: plain.slice(0, 80) || 'Untitled',
            preview: '',
        };
    }

    return {
        title: texts[0],
        preview: texts[1] || '',
    };
};

/** Split stored note HTML into title (first heading) and body for the editor. */
export const splitNoteContent = html => {
    if (!html) return { title: '', bodyHtml: '' };

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const first = doc.body.firstElementChild;

    if (first && (first.tagName === 'H1' || first.tagName === 'H2')) {
        const title = first.textContent?.trim() || '';
        first.remove();
        return { title, bodyHtml: doc.body.innerHTML.trim() };
    }

    const children = Array.from(doc.body.children);
    const firstContentBlock = children.find(el => (el.textContent || '').trim());

    if (firstContentBlock) {
        const title = firstContentBlock.textContent.trim();
        firstContentBlock.remove();
        return { title, bodyHtml: doc.body.innerHTML.trim() };
    }

    const plain = (doc.body.textContent || '').trim();
    if (plain) {
        return { title: plain, bodyHtml: '' };
    }

    return { title: '', bodyHtml: html };
};

/** Title for breadcrumbs/chrome; falls back to stored note HTML when state is empty. */
export const resolveEntryTitle = (entryTitle, noteHtml) => {
    const trimmed = (entryTitle || '').trim();
    if (trimmed) return trimmed;

    if (!noteHtml) return '';

    const { title } = splitNoteContent(noteHtml);
    if (title) return title;

    const { title: previewTitle } = getNotePreview(noteHtml);
    return previewTitle === 'Untitled' ? '' : previewTitle;
};

const TITLE_MAX_LENGTH = 45;

const escapeHtml = text =>
    String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const plainTextToBodyHtml = text => {
    const normalized = (text || '').replace(/\r\n/g, '\n').trim();
    if (!normalized) return '';

    return normalized
        .split(/\n{2,}/)
        .map(block => {
            const withBreaks = escapeHtml(block).replace(/\n/g, '<br>');
            return `<p>${withBreaks}</p>`;
        })
        .join('');
};

/** Light cleanup for Quill — keep structure/styles Quill's clipboard can map to formats. */
export const sanitizePastedHtmlForQuill = html => {
    if (!html?.trim()) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('style, meta, link, script, title').forEach(el => el.remove());

    const headingLevelFromWordish = el => {
        const tag = el.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag)) {
            // Journal toolbar uses 2/3/4; map h1→2, h5/h6→4
            const level = Number(tag[1]);
            if (level <= 1) return 2;
            if (level >= 5) return 4;
            return Math.min(level, 4);
        }

        const className = `${el.getAttribute('class') || ''}`.toLowerCase();
        const style = `${el.getAttribute('style') || ''}`.toLowerCase();

        // Word / Google Docs heading class hints
        if (/\b(mso-?title|title)\b/.test(className) && !/subtitle/.test(className)) return 2;
        if (/heading\s*_?1|mso-?heading.?1|heading-1/.test(className)) return 2;
        if (/heading\s*_?2|mso-?heading.?2|heading-2/.test(className)) return 2;
        if (/heading\s*_?3|mso-?heading.?3|heading-3/.test(className)) return 3;
        if (/heading\s*_?4|mso-?heading.?4|heading-4|subtitle/.test(className)) return 4;
        if (/\bheading\b/.test(className)) return 2;

        // Font-size + bold fallback — common when Word pastes styled paragraphs without heading tags
        const sizeMatch = style.match(/font-size:\s*([\d.]+)(pt|px)/i);
        const isBold =
            /font-weight:\s*(bold|[6-9]00)/i.test(style) ||
            el.querySelector('b, strong') != null ||
            /^h[1-6]$/i.test(el.tagName);
        if (sizeMatch) {
            const value = parseFloat(sizeMatch[1]);
            const px = sizeMatch[2].toLowerCase() === 'pt' ? value * (96 / 72) : value;
            if (px >= 22) return 2;
            if (px >= 18 && isBold) return 3;
            if (px >= 16 && isBold) return 4;
        }

        return null;
    };

    const promoteToHeading = el => {
        const level = headingLevelFromWordish(el);
        if (!level) return;
        if (/^h[1-6]$/i.test(el.tagName) && Number(el.tagName[1]) === level) return;

        const heading = doc.createElement(`h${level}`);
        while (el.firstChild) heading.appendChild(el.firstChild);
        el.replaceWith(heading);
    };

    // Normalize headings + Word/Docs "heading-looking" paragraphs/divs
    Array.from(doc.body.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div')).forEach(promoteToHeading);

    // Promote common doc inline styles to semantic tags Quill keeps reliably
    doc.querySelectorAll('span, font').forEach(el => {
        const style = `${el.getAttribute('style') || ''} ${el.getAttribute('face') || ''}`.toLowerCase();
        const weight =
            style.includes('font-weight:700') ||
            style.includes('font-weight:bold') ||
            style.includes('font-weight: 700') ||
            style.includes('font-weight: bold');
        const italic = style.includes('font-style:italic') || style.includes('font-style: italic');
        const underline =
            style.includes('text-decoration:underline') ||
            style.includes('text-decoration: underline') ||
            style.includes('underline');

        let wrapper = null;
        if (weight) wrapper = doc.createElement('strong');
        else if (italic) wrapper = doc.createElement('em');
        else if (underline) wrapper = doc.createElement('u');

        if (wrapper) {
            while (el.firstChild) wrapper.appendChild(el.firstChild);
            el.appendChild(wrapper);
        }
    });

    return doc.body.innerHTML.trim();
};

const isMeaningfulBlock = el => {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
    if (el.querySelector('img')) return true;
    return Boolean((el.textContent || '').trim());
};

/** Unwrap Google Docs / Word single wrappers so we see real paragraphs. */
const getPastedBlocks = root => {
    let blocks = Array.from(root.children).filter(isMeaningfulBlock);
    const wrappers = new Set(['B', 'STRONG', 'SPAN', 'DIV', 'FONT', 'CENTER']);

    let guard = 0;
    while (blocks.length === 1 && wrappers.has(blocks[0].tagName) && blocks[0].children.length > 0 && guard < 6) {
        const inner = Array.from(blocks[0].children).filter(isMeaningfulBlock);
        if (inner.length === 0) break;
        blocks = inner;
        guard += 1;
    }

    return blocks;
};

/** Soft-trim title to max length at a word/sentence boundary. */
const splitAtMaxLength = normalized => {
    if (normalized.length <= TITLE_MAX_LENGTH) return { title: normalized, overflow: '' };

    const window = normalized.slice(0, TITLE_MAX_LENGTH);
    const sentenceMatch = window.match(/^(.{8,}?[.!?])(\s|$)/);
    if (sentenceMatch?.[1]) {
        const title = sentenceMatch[1].trim();
        return { title, overflow: normalized.slice(title.length).trim() };
    }

    const lastSpace = window.lastIndexOf(' ');
    const cutAt = lastSpace > 20 ? lastSpace : TITLE_MAX_LENGTH;
    const title = normalized.slice(0, cutAt).trim();
    return { title, overflow: normalized.slice(cutAt).trim() };
};

/** Cap title at ~45 chars; prefer heading/sentence boundary. Rest becomes overflow. */
export const splitLongPlainTitle = text => {
    const normalized = (text || '').trim().replace(/\s+/g, ' ');
    if (!normalized) return { title: '', overflow: '' };

    // Lost line-break between heading and body: "What is an AI citation?So a citation..."
    const lostBreak = normalized.match(/^(.{8,120}?[.!?])([A-Z].*)$/);
    if (lostBreak) {
        const titleCandidate = lostBreak[1].trim();
        if (titleCandidate.length <= 80) {
            const { title, overflow: titleOverflow } = splitAtMaxLength(titleCandidate);
            const rest = `${titleOverflow} ${lostBreak[2]}`.trim();
            return { title, overflow: rest };
        }
    }

    if (normalized.length <= TITLE_MAX_LENGTH) return { title: normalized, overflow: '' };
    return splitAtMaxLength(normalized);
};

/** Split a single HTML block when the first child looks like a heading and siblings are body. */
const splitBlockByInlineHeading = block => {
    if (!block) return null;

    const children = Array.from(block.childNodes).filter(node => {
        if (node.nodeType === Node.TEXT_NODE) return Boolean(node.textContent?.trim());
        if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.tagName === 'BR') return true;
            return Boolean(node.textContent?.trim());
        }
        return false;
    });

    if (children.length < 2) return null;

    const isHeadingish = node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        const tag = node.tagName.toLowerCase();
        if (/^h[1-6]$/.test(tag) || tag === 'strong' || tag === 'b') return true;
        const style = `${node.getAttribute?.('style') || ''}`.toLowerCase();
        const sizeMatch = style.match(/font-size:\s*([\d.]+)(pt|px)/i);
        const bold = /font-weight:\s*(bold|[6-9]00)/i.test(style);
        if (sizeMatch) {
            const value = parseFloat(sizeMatch[1]);
            const px = sizeMatch[2].toLowerCase() === 'pt' ? value * (96 / 72) : value;
            if (px >= 18) return true;
        }
        return bold && (node.textContent || '').trim().length <= 80;
    };

    let splitIndex = -1;
    if (isHeadingish(children[0])) {
        for (let i = 1; i < children.length; i += 1) {
            const node = children[i];
            if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
                splitIndex = i;
                break;
            }
            if (!isHeadingish(node)) {
                splitIndex = i;
                break;
            }
        }
    }

    if (splitIndex <= 0) return null;

    const title = children
        .slice(0, splitIndex)
        .filter(n => !(n.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR'))
        .map(n => n.textContent || '')
        .join('')
        .trim();

    const restNodes = children.slice(splitIndex).filter(n => !(n.nodeType === Node.ELEMENT_NODE && n.tagName === 'BR'));
    if (!title || restNodes.length === 0) return null;

    const restWrap = block.ownerDocument.createElement('p');
    restNodes.forEach(n => restWrap.appendChild(n.cloneNode(true)));
    return { rawTitle: title, restBodyHtml: restWrap.outerHTML };
};

/**
 * Split pasted clipboard content so only a short heading stays in the title field.
 * Prefers HTML so body keeps original formatting; falls back to plain text.
 */
export const splitPastedContentIntoTitleAndBody = (html, plainText) => {
    const text = (plainText || '').replace(/\r\n/g, '\n');
    const cleanedHtml = sanitizePastedHtmlForQuill(html);
    const hasHtml = Boolean(cleanedHtml);

    const finalize = (rawTitle, restBodyHtml = '', restPlain = '') => {
        const { title, overflow } = splitLongPlainTitle(rawTitle);
        const overflowHtml = overflow ? plainTextToBodyHtml(overflow) : '';
        const bodyHtml = [overflowHtml, restBodyHtml, restPlain ? plainTextToBodyHtml(restPlain) : ''].filter(Boolean).join('');
        const shouldSplit = Boolean(overflow || restBodyHtml || restPlain);
        return { shouldSplit, title, bodyHtml: sanitizePastedHtmlForQuill(bodyHtml) || bodyHtml };
    };

    // Plain text often keeps the heading line-break when HTML collapses it
    const plainLines = text
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

    if (hasHtml) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanedHtml, 'text/html');
        const blocks = getPastedBlocks(doc.body);

        if (blocks.length > 1) {
            const first = blocks[0];
            const rawTitle = (first.textContent || '').trim();
            first.remove();
            return finalize(rawTitle, sanitizePastedHtmlForQuill(doc.body.innerHTML));
        }

        if (blocks.length === 1) {
            const block = blocks[0];
            const htmlWithBreaks = block.innerHTML || '';
            const hasBreaks = /<br\s*\/?>/i.test(htmlWithBreaks) || /\n/.test(block.textContent || '');

            if (hasBreaks) {
                const parts = htmlWithBreaks.split(/<br\s*\/?>/i);
                const rawTitle = (parts[0] || '').replace(/<[^>]+>/g, '').trim();
                const restInner = parts.slice(1).join('<br>').trim();
                const tag = /^h[1-6]$/i.test(block.tagName) ? 'p' : block.tagName.toLowerCase();
                const restBodyHtml = restInner ? `<${tag}>${restInner}</${tag}>` : '';
                return finalize(rawTitle, restBodyHtml);
            }

            const inlineSplit = splitBlockByInlineHeading(block);
            if (inlineSplit) {
                return finalize(inlineSplit.rawTitle, inlineSplit.restBodyHtml);
            }

            // HTML lost the line break but plain text still has heading on its own line
            if (plainLines.length > 1) {
                const rawTitle = plainLines[0];
                const blockText = (block.textContent || '').replace(/\s+/g, ' ').trim();
                const normalizedTitle = rawTitle.replace(/\s+/g, ' ').trim();
                if (blockText.startsWith(normalizedTitle) || blockText.startsWith(normalizedTitle.replace(/\s/g, ''))) {
                    let remainder = blockText.slice(normalizedTitle.length).trim();
                    // Handle mashed "?So" where plain had a break but HTML didn't
                    if (!remainder && blockText.includes(normalizedTitle.replace(/\s/g, ''))) {
                        remainder = blockText.replace(normalizedTitle, '').replace(normalizedTitle.replace(/\s/g, ''), '').trim();
                    }
                    if (!remainder) {
                        const lost = splitLongPlainTitle(blockText);
                        return finalize(lost.title || rawTitle, lost.overflow ? `<p>${escapeHtml(lost.overflow)}</p>` : '');
                    }
                    return finalize(rawTitle, `<p>${escapeHtml(remainder)}</p>`);
                }
                return finalize(rawTitle, '', plainLines.slice(1).join('\n'));
            }

            return finalize(block.textContent || '');
        }
    }

    if (plainLines.length > 1) {
        return finalize(plainLines[0], '', plainLines.slice(1).join('\n'));
    }

    return finalize(text.trim());
};

/** Merge title field + Quill body back into stored note HTML. */
export const mergeNoteContent = (title, bodyHtml) => {
    const container = document.createElement('div');
    const trimmedTitle = (title || '').trim();

    if (trimmedTitle) {
        const h2 = document.createElement('h2');
        const strong = document.createElement('strong');
        strong.textContent = trimmedTitle;
        h2.appendChild(strong);
        container.appendChild(h2);
    }

    if (bodyHtml?.trim()) {
        const bodyWrap = document.createElement('div');
        bodyWrap.innerHTML = bodyHtml;
        while (bodyWrap.firstChild) {
            container.appendChild(bodyWrap.firstChild);
        }
    }

    return container.innerHTML;
};

export const noteHasContent = (title, bodyHtml) => {
    const trimmedTitle = (title || '').trim();
    const bodyText = (bodyHtml || '').replace(/<[^>]*>/g, '').trim();
    return Boolean(trimmedTitle || bodyText);
};

/** Figma-style date: "16 Mar, 11:08" */
export const formatCardDate = dateString => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day} ${month}, ${hours}:${minutes}`;
    } catch {
        return dateString;
    }
};

/** Best available last-updated timestamp for a note (API may only expose created). */
export const getNoteLastUpdated = note => note?.updated_at || note?.updated || note?.created;

/** Relative last-updated label: "Just now", "5 mins ago", "1 hour ago", then absolute date. */
export const formatLastUpdated = dateString => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return '';

        const diffMs = Date.now() - date.getTime();
        if (diffMs < 0) return 'Just now';

        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        const diffWeek = Math.floor(diffDay / 7);

        if (diffSec < 60) return 'Just now';
        if (diffMin < 60) return diffMin === 1 ? '1 min ago' : `${diffMin} mins ago`;
        if (diffHour < 24) return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
        if (diffDay < 7) return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`;
        if (diffDay < 30) return diffWeek === 1 ? '1 week ago' : `${diffWeek} weeks ago`;

        return formatCardDate(dateString);
    } catch {
        return dateString;
    }
};

/** Short display name: "Ethan B." */
export const getShortDisplayName = fullName => {
    if (!fullName || typeof fullName !== 'string') return '';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

/** Visibility subtitle for section rows / doc meta. */
export const getSectionVisibility = section => {
    if (!section) return 'Private';
    if (section.is_project_default) return 'Shared with project';
    if (section.is_shared && section.owner_name) {
        const ownerShort = getShortDisplayName(section.owner_name) || section.owner_name;
        return `Shared with ${ownerShort}`;
    }
    if (section.shared_with && section.shared_with.length > 0) {
        const names = Array.isArray(section.shared_with_names)
            ? section.shared_with_names.map(n => (typeof n === 'string' ? n.trim() : '')).filter(Boolean)
            : [];

        if (names.length === 1) {
            const short = getShortDisplayName(names[0]) || names[0];
            return `Shared with ${short}`;
        }

        if (names.length > 1) {
            const firstName = names[0].split(/\s+/).filter(Boolean)[0] || names[0];
            return `Shared with ${firstName} and more`;
        }

        // Names not loaded yet — fall back to count from shared_with ids
        if (section.shared_with.length === 1) return 'Shared with 1 person';
        return `Shared with ${section.shared_with.length} people`;
    }
    return 'Private';
};

export const isSectionPrivate = section => getSectionVisibility(section) === 'Private';
